import React, { useEffect, useState, useCallback } from 'react'
import { Box, Heading, Text, Button, Stack, EmptyBox, Wrap, WrapItem, ParticipantNoteEditor } from '@/components/lib'

import ParticipantNotePostIt from './ParticipantNotePostIt'
import api from '../../api'
import utils from '../../utils/utils'
import useStore from '../../store'

export default function ParticipantNotes ({ participant }) {
  const { addParticipantNote, setParticipantNotesForParticipant, getParticipantNotesForParticipant, trial, user } = useStore()
  const participantId = participant?._id
  const notes = getParticipantNotesForParticipant(participantId)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorNote, setEditorNote] = useState(null)

  useEffect(() => {
    if (!participant || !utils.canReadParticipantNotes(user, trial)) return
    api.participantNotes.listForParticipant(participant.trial, participant._id, (data) => {
      setParticipantNotesForParticipant(participant._id, data?.notes || [])
    })
  }, [participant, setParticipantNotesForParticipant, user, trial])

  const openNewNote = useCallback(() => {
    setEditorNote(null)
    setEditorOpen(true)
  }, [])

  const openNote = useCallback((note) => {
    setEditorNote(note)
    setEditorOpen(true)
  }, [])

  const closeEditor = useCallback(() => {
    setEditorOpen(false)
    setEditorNote(null)
  }, [])

  const onSaved = useCallback((saved) => {
    if (saved) addParticipantNote(saved)
  }, [addParticipantNote])

  const canReadNotes = utils.canReadParticipantNotes(user, trial)
  const canWriteNotes = utils.canWriteParticipantNotes(user, trial)

  const notesNewestFirst = [...(notes || [])].sort((a, b) => {
    const ta = new Date(a.createdAt).getTime()
    const tb = new Date(b.createdAt).getTime()
    return tb - ta
  })

  if (!canReadNotes && !canWriteNotes) {
    return null
  }

  return (
    <Stack
      gap={5}
      borderWidth='1px'
      borderStyle='dashed'
      borderColor='border.subtle'
      bg='bg.muted'
      rounded='lg'
      p={4}
      boxShadow='inset 0 1px 2px rgba(0,0,0,0.04)'
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Box>
          <Heading size='md'>Notes</Heading>
        </Box>
        {canWriteNotes &&
          <Button colorPalette='primary' size='sm' variant='outline' onClick={openNewNote}>
            New note
          </Button>}
      </Stack>

      <ParticipantNoteEditor
        participant={participant}
        note={editorNote}
        isOpen={editorOpen}
        onClose={closeEditor}
        onSaved={onSaved}
      />

      {!canReadNotes && (
        <Text fontSize='sm' color='fg.muted'>You don&apos;t have permission to view participant notes.</Text>
      )}

      {canReadNotes && notes?.length === 0 &&
        <EmptyBox icon='notes' title='Participant notes' description='Notes about this participant will appear here once created.' />}

      {canReadNotes && notesNewestFirst.length > 0 && (
        <Wrap gap={4} maxH='500px' overflowY='auto' p={1} align='stretch'>
          {notesNewestFirst.map((note, index) => (
            <WrapItem key={note._id}>
              <ParticipantNotePostIt note={note} onSelect={openNote} index={index} />
            </WrapItem>
          ))}
        </Wrap>
      )}
    </Stack>
  )
}
